import {
  FileTextOutlined,
  InboxOutlined,
  PaperClipOutlined
} from "@ant-design/icons";
import { useSubscription, type ApolloClient } from "@apollo/client";
import {
  Button,
  Divider,
  Drawer,
  message,
  Spin,
  Upload,
  type UploadFile,
  type UploadProps
} from "antd";
import Dragger from "antd/lib/upload/Dragger";
import { useEffect, useMemo, useState } from "react";
import { SUBSCRIPTION_IMPORT_FILE_PROCESSING } from "services/graphQL/subscriptions";
import { downloadFileInBrowser } from "utils/utils";
import "./import-scope-items-csv-drawer.scss";
import {
  downloadProjectDesignScopeImportTemplate,
  getFilenameFromContentDisposition,
  importProjectDesignScopeCsv,
  SCOPE_ITEMS_IMPORT_TEMPLATE_FILENAME,
  triggerBlobFileDownload,
  type ProjectDesignScopeImportResponse
} from "./apis";
import ImportLogSummaryPanel, {
  normalizeImportLogStats,
  type ScopeItemsImportLogStats
} from "./import-log-summary-panel";
import { SCOPE_ITEMS_IMPORT_INSTRUCTIONS } from "./template";
import {
  SCOPE_ITEMS_CSV_FILE_ERROR,
  SCOPE_ITEMS_IMPORT_ACTION_ERROR,
  scopeItemsImportErrorMessage
} from "./toast-messages";

export type ImportScopeItemsCsvDrawerProps = {
  open: boolean;
  projectToken: string;
  gqlClient: ApolloClient<object> | undefined;
  onClose: () => void;
  /** Called after a successful import so the parent can reload the list. */
  onImported?: () => void;
};

type UploadingState = {
  uploading: boolean;
  processingStatus: string;
  processedResults: ScopeItemsImportLogStats | null;
};

function isTerminalImportStatus(status: string): boolean {
  // Materials uses PROCESSED/FAILED; Design Scope import_log also uses COMPLETED.
  return (
    status === "PROCESSED" || status === "COMPLETED" || status === "FAILED"
  );
}

function isSuccessfulImportStatus(status: string): boolean {
  return status === "PROCESSED" || status === "COMPLETED";
}

function parseImportLogStats(
  statusComment: string | null | undefined
): ScopeItemsImportLogStats | null {
  if (!statusComment?.trim()) return null;
  try {
    const parsed = JSON.parse(statusComment);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return normalizeImportLogStats(parsed as Record<string, unknown>);
    }
  } catch {
    // Intermediate plain-text comments are ignored until a terminal status.
  }
  return null;
}

/**
 * Project-level Design Scope CSV import drawer.
 * UI mirrors the subscription CSV import drawer; kept as a separate copy.
 * Async import + import_log polling matches Materials (`ImportFeatureLogs`).
 */
function ImportScopeItemsCsvDrawer({
  open,
  projectToken,
  gqlClient,
  onClose,
  onImported
}: ImportScopeItemsCsvDrawerProps) {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [importLogId, setImportLogId] = useState("");
  const [uploadingState, setUploadingState] = useState<UploadingState>({
    uploading: false,
    processingStatus: "",
    processedResults: null
  });

  const isImportInProgress =
    uploadingState.uploading ||
    uploadingState.processingStatus === "PROCESSING";
  // Materials shows the in-drawer summary whenever import_log is terminal.
  const showSummary = isTerminalImportStatus(uploadingState.processingStatus);

  const resetFormState = () => {
    setFileList([]);
    setDownloadingTemplate(false);
    setImportLogId("");
    setUploadingState({
      uploading: false,
      processingStatus: "",
      processedResults: null
    });
  };

  useEffect(() => {
    if (open) return;
    resetFormState();
  }, [open]);

  const { data: fileProcessingData } = useSubscription(
    SUBSCRIPTION_IMPORT_FILE_PROCESSING,
    {
      shouldResubscribe: true,
      client: gqlClient,
      skip: !importLogId || !gqlClient,
      variables: {
        where: { id: { _eq: importLogId } }
      }
    }
  );

  useEffect(() => {
    if (!fileProcessingData?.import_log?.length) return;

    const row = fileProcessingData.import_log[0];
    const processingStatus = row.status as string;
    const stats = parseImportLogStats(row.status_comment);
    const terminal = isTerminalImportStatus(processingStatus);

    // Keep waiting on non-JSON intermediate comments while still PROCESSING.
    if (!terminal && !stats) return;

    setUploadingState({
      uploading: false,
      processingStatus,
      processedResults: stats
    });

    if (!terminal) return;

    if (stats?.File) {
      downloadFileInBrowser(stats.File, "Failed_Design_Scope_Log.csv");
    }
    if (isSuccessfulImportStatus(processingStatus)) {
      onImported?.();
    }
    // Match Materials: react only to new subscription payloads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileProcessingData]);

  const selectedFile = fileList[0];
  const canImport =
    Boolean(selectedFile && projectToken) &&
    !isImportInProgress &&
    !showSummary;

  const uploadProps: UploadProps = useMemo(
    () => ({
      accept: ".csv,text/csv",
      multiple: false,
      maxCount: 1,
      showUploadList: false,
      beforeUpload: (file) => {
        const isCsv =
          file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv");
        if (!isCsv) {
          message.error(SCOPE_ITEMS_CSV_FILE_ERROR);
          return Upload.LIST_IGNORE;
        }
        setFileList([
          {
            uid: file.uid,
            name: file.name,
            status: "done",
            originFileObj: file
          }
        ]);
        return false;
      },
      onRemove: () => {
        setFileList([]);
      }
    }),
    []
  );

  const handleDownloadTemplate = async () => {
    if (downloadingTemplate || !projectToken) return;
    setDownloadingTemplate(true);
    try {
      const response =
        await downloadProjectDesignScopeImportTemplate(projectToken);
      if (!response.success || !(response.data instanceof Blob)) {
        message.error(scopeItemsImportErrorMessage(response.errorMsg));
        return;
      }
      const contentDisposition = response.response?.headers?.[
        "content-disposition"
      ] as string | undefined;
      const fileName = getFilenameFromContentDisposition(
        contentDisposition,
        SCOPE_ITEMS_IMPORT_TEMPLATE_FILENAME
      );
      triggerBlobFileDownload(response.data, fileName);
    } catch {
      message.error(SCOPE_ITEMS_IMPORT_ACTION_ERROR);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleClose = () => {
    if (isImportInProgress) return;
    onClose();
  };

  const handleBackToUpload = () => {
    setUploadingState({
      uploading: false,
      processingStatus: "",
      processedResults: null
    });
    setImportLogId("");
    setFileList([]);
  };

  const handleImport = async () => {
    if (!canImport || !selectedFile?.originFileObj || !projectToken) return;

    setUploadingState({
      uploading: true,
      processingStatus: "",
      processedResults: null
    });

    try {
      const response = await importProjectDesignScopeCsv({
        file: selectedFile.originFileObj as File,
        projectToken
      });

      if (!response.success) {
        setUploadingState({
          uploading: false,
          processingStatus: "",
          processedResults: null
        });
        message.error(scopeItemsImportErrorMessage(response.errorMsg));
        return;
      }

      const data = response.data as
        | ProjectDesignScopeImportResponse
        | undefined;
      if (!data?.id) {
        setUploadingState({
          uploading: false,
          processingStatus: "",
          processedResults: null
        });
        message.error(SCOPE_ITEMS_IMPORT_ACTION_ERROR);
        return;
      }

      setUploadingState({
        uploading: false,
        processingStatus: "PROCESSING",
        processedResults: null
      });
      setImportLogId(data.id);
      setFileList([]);
    } catch {
      setUploadingState({
        uploading: false,
        processingStatus: "",
        processedResults: null
      });
      message.error(SCOPE_ITEMS_IMPORT_ACTION_ERROR);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title="Import Scope Items"
      size={480}
      destroyOnHidden
      closable={!isImportInProgress}
      mask={{ closable: !isImportInProgress }}
      keyboard={!isImportInProgress}
      className="scope-items-csv-import-drawer"
      data-testid="scope-items-csv-import-drawer"
      footer={
        <div className="flex justify-end gap-2">
          <Button
            onClick={handleClose}
            disabled={isImportInProgress}
            data-testid="scope-items-csv-import-cancel"
          >
            {showSummary ? "Close" : "Cancel"}
          </Button>
          {!isImportInProgress && !showSummary && (
            <Button
              type="primary"
              className="bg-ciq-primary-800 border-ciq-primary-800"
              disabled={!canImport}
              loading={uploadingState.uploading}
              onClick={() => void handleImport()}
              data-testid="scope-items-csv-import-submit"
            >
              Import
            </Button>
          )}
        </div>
      }
    >
      {showSummary ? (
        <ImportLogSummaryPanel
          stats={uploadingState.processedResults ?? {}}
          description="Design Scope import complete. Review the summary below."
          onBack={handleBackToUpload}
          backLabel="Back to upload"
        />
      ) : (
        <div className="flex flex-col gap-5">
          <section data-testid="scope-items-csv-download-format">
            <div className="font-semibold text-ciq-gray-950">
              Download Format
            </div>
            <div className="mt-1 text-xs text-black text-opacity-50">
              Download pre-formatted .csv file to import Scope items
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 rounded border border-solid border-ciq-gray-200 px-3 py-2.5">
              <div className="flex min-w-0 items-center gap-2">
                <FileTextOutlined className="text-base text-ciq-primary-800" />
                <span className="truncate text-sm text-ciq-gray-950">
                  Standard Format .csv
                </span>
              </div>
              <Button
                loading={downloadingTemplate}
                disabled={
                  downloadingTemplate || isImportInProgress || !projectToken
                }
                onClick={() => void handleDownloadTemplate()}
                data-testid="scope-items-csv-download-template"
              >
                Download
              </Button>
            </div>
          </section>

          <section data-testid="scope-items-csv-instructions">
            <div className="font-semibold text-ciq-gray-950">Instructions</div>
            <ul className="mt-2 list-disc space-y-1 pl-5 m-0 text-xs text-black text-opacity-60">
              {SCOPE_ITEMS_IMPORT_INSTRUCTIONS.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </section>

          <Divider className="my-0" />

          <section data-testid="scope-items-csv-upload-section">
            <div className="font-semibold text-ciq-gray-950">Upload file</div>
            <div className="mt-3">
              <Dragger
                {...uploadProps}
                disabled={isImportInProgress}
                data-testid="scope-items-csv-upload-dragger"
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">Click or drag file to upload</p>
                <p className="ant-upload-hint">Only CSV files are supported</p>
              </Dragger>
            </div>
          </section>

          {selectedFile && (
            <section
              className="rounded border border-solid border-ciq-gray-200 p-3"
              data-testid="scope-items-csv-file-details"
            >
              <div className="flex items-center gap-2 text-sm font-medium text-ciq-gray-950">
                <PaperClipOutlined />
                <span className="truncate" title={selectedFile.name}>
                  {selectedFile.name}
                </span>
              </div>
            </section>
          )}

          {isImportInProgress && (
            <div
              className="flex flex-col items-center gap-2 py-4"
              data-testid="scope-items-csv-import-processing"
            >
              <Spin size="medium" />
              <p className="m-0 text-sm font-semibold">Import in progress</p>
              <p className="m-0 text-center text-xs text-black text-opacity-50">
                Import may take a couple of minutes. Please remain on this page
                until import is complete
              </p>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

export default ImportScopeItemsCsvDrawer;
